// Bank statement PDF parser. Phase 2 of the Bank Statement Upload
// feature. Pluggable: parseBankStatement(text, bankName) routes to a
// bank-specific parser. Currently only FNB is implemented.
//
// All parsers consume *plain text* (already extracted from the PDF
// via extractPdfText in src/lib/pdfPosExtract.js) and return the
// canonical shape declared in Phase 0. The parser is pure — no
// Firestore writes, no DOM access — so it can be unit-tested without
// mocking the storage layer.

import { extractPdfText } from './pdfPosExtract'
import { suggestCategory, DEFAULT_EXPENSE_CATEGORIES } from './bankStore'

// ─── Constants ────────────────────────────────────────────────────

const MONTHS = {
  jan: '01', january: '01',
  feb: '02', february: '02',
  mar: '03', march: '03',
  apr: '04', april: '04',
  may: '05',
  jun: '06', june: '06',
  jul: '07', july: '07',
  aug: '08', august: '08',
  sep: '09', sept: '09', september: '09',
  oct: '10', october: '10',
  nov: '11', november: '11',
  dec: '12', december: '12',
}

// Lines that are never transactions and never charge headers.
const SKIP_PATTERNS = [
  /^closing\s+balance/i,
  /^opening\s+balance/i,
  /^no\.\s+credit\s+transactions/i,
  /^no\.\s+debit\s+transactions/i,
  /^first\s+national\s+bank/i,
  /^date\s+description/i,
  /^statement\s+period/i,
  /^statement\s+date/i,
  /^branch\s+number/i,
  /^account\s+number/i,
]

// ─── Helpers ──────────────────────────────────────────────────────

/** "3,670.00" → 3670, "1,234.56" → 1234.56, "abc" → NaN */
export function parseAmount(raw) {
  if (raw == null) return NaN
  const cleaned = String(raw).replace(/[R,\s]/g, '')
  const n = parseFloat(cleaned)
  return Number.isFinite(n) ? n : NaN
}

/** Parse "23 February 2026" or "23 Feb 2026" → "2026-02-23". */
export function parseLongDate(text) {
  const m = String(text || '').trim().match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/)
  if (!m) return null
  const day = m[1].padStart(2, '0')
  const month = MONTHS[m[2].toLowerCase()]
  if (!month) return null
  return `${m[3]}-${month}-${day}`
}

/** Reconstruct "23 Feb" + statement year → "2026-02-23". */
export function reconstructDate(shortDate, year) {
  const m = String(shortDate || '').trim().match(/^(\d{1,2})\s+([A-Za-z]+)$/)
  if (!m) return null
  const day = m[1].padStart(2, '0')
  const month = MONTHS[m[2].toLowerCase()]
  if (!month || !year) return null
  return `${year}-${month}-${day}`
}

/** Should this line be ignored entirely? */
export function isSkipLine(line) {
  const s = String(line || '').trim()
  if (!s) return true
  return SKIP_PATTERNS.some(re => re.test(s))
}

// ─── FNB statement parser ─────────────────────────────────────────

/** Extract { from, to, year } from a "Statement Period : ..." line.
 *  Returns null on no match. */
export function parseFnbStatementPeriod(text) {
  const m = String(text || '').match(/Statement\s+Period\s*:\s*(\d{1,2}\s+[A-Za-z]+\s+\d{4})\s+to\s+(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i)
  if (!m) return null
  const from = parseLongDate(m[1])
  const to = parseLongDate(m[2])
  if (!from || !to) return null
  return { from, to, year: from.slice(0, 4) }
}

/** Extract account number from any of the FNB header variants. Returns
 *  null if no match. */
export function parseFnbAccountNumber(text) {
  const t = String(text || '')
  let m = t.match(/Gold\s+Business\s+Account\s*:\s*(\d{6,})/i)
  if (m) return m[1]
  m = t.match(/Account\s+Number\s*:?\s*(\d{6,})/i)
  if (m) return m[1]
  return null
}

/** Extract account name (the line *before* "Gold Business Account" if
 *  present, otherwise null). FNB statements display the holder name
 *  immediately above the account header. */
export function parseFnbAccountName(lines) {
  for (let i = 0; i < lines.length; i++) {
    if (/Gold\s+Business\s+Account/i.test(lines[i])) {
      // Walk back for the first non-empty non-header line
      for (let j = i - 1; j >= 0 && j >= i - 5; j--) {
        const candidate = String(lines[j] || '').trim()
        if (!candidate) continue
        if (/^statement|^branch|^account|^period|^date/i.test(candidate)) continue
        return candidate
      }
      return null
    }
  }
  return null
}

/** Parse one transaction line. Returns:
 *    { date: 'YYYY-MM-DD', description, amount, type: 'credit'|'debit',
 *      accruedCharge: number|null }
 *  or null if the line is not a transaction. `year` is needed because
 *  FNB rows only carry the day-month. */
export function parseFnbTransactionLine(line, year) {
  const raw = String(line || '').trim()
  if (!raw) return null
  if (isSkipLine(raw)) return null

  // Pattern:
  //   "23 Feb  Description text...   1,234.56Cr   5,678.90Cr   12.34"
  //   date  desc  amount(Cr|Dr)  balance(Cr|Dr)  [accrued?]
  const re = /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(.+?)\s+([\d,]+\.\d{2})(Cr|Dr)\s+([\d,]+\.\d{2})(Cr|Dr)(?:\s+([\d,]+\.\d{2}))?\s*$/i
  const m = raw.match(re)
  if (!m) return null

  const day = m[1].padStart(2, '0')
  const monthKey = m[2].toLowerCase()
  const month = MONTHS[monthKey]
  if (!month || !year) return null

  const description = m[3].trim()
  if (!description) return null
  // Reject lines whose "description" is actually a header keyword
  if (/^(closing|opening)\s+balance$/i.test(description)) return null

  const amount = parseAmount(m[4])
  if (!Number.isFinite(amount)) return null
  const suffix = m[5].toLowerCase()
  const type = suffix === 'cr' ? 'credit' : 'debit'

  const accruedRaw = m[8]
  const accruedCharge = accruedRaw != null ? parseAmount(accruedRaw) : null

  return {
    date: `${year}-${month}-${day}`,
    description,
    amount: Math.abs(amount),
    type,
    accruedCharge: Number.isFinite(accruedCharge) ? accruedCharge : null,
  }
}

/** Extract the four FNB summary bank-charge totals from raw lines.
 *  Returns { serviceFees, cashDepositFees, cashHandlingFees, otherFees }
 *  with each value defaulting to 0. */
export function parseFnbBankCharges(lines) {
  const out = { serviceFees: 0, cashDepositFees: 0, cashHandlingFees: 0, otherFees: 0 }
  for (const rawLine of lines) {
    const line = String(rawLine || '').trim()
    if (!line) continue
    let m
    if ((m = line.match(/^Service\s+Fees?\s+([\d,]+\.\d{2})/i))) {
      out.serviceFees = parseAmount(m[1]) || 0
    } else if ((m = line.match(/^Cash\s+Deposit\s+Fees?\s+([\d,]+\.\d{2})/i))) {
      out.cashDepositFees = parseAmount(m[1]) || 0
    } else if ((m = line.match(/^Cash\s+Handling\s+Fees?\s+([\d,]+\.\d{2})/i))) {
      out.cashHandlingFees = parseAmount(m[1]) || 0
    } else if ((m = line.match(/^Other\s+Fees?\s+([\d,]+\.\d{2})/i))) {
      out.otherFees = parseAmount(m[1]) || 0
    }
  }
  return out
}

/** Extract opening + closing balances. Returns { opening, closing }
 *  (each may be null). The "Cr" / "Dr" suffix is ignored — opening
 *  and closing balances are stored as positive numbers. */
export function parseFnbBalances(lines) {
  const out = { opening: null, closing: null }
  for (const rawLine of lines) {
    const line = String(rawLine || '').trim()
    let m
    if (out.opening == null && (m = line.match(/^Opening\s+Balance\s+([\d,]+\.\d{2})/i))) {
      out.opening = parseAmount(m[1])
    }
    if ((m = line.match(/^Closing\s+Balance\s+([\d,]+\.\d{2})/i))) {
      // Last match wins — closing balance appears in both summary and
      // at the foot of the transactions table.
      out.closing = parseAmount(m[1])
    }
  }
  return out
}

/** Top-level FNB parser. Consumes pre-split lines, returns the
 *  canonical bank-statement shape. */
export function parseFnbStatement(lines, { categories } = {}) {
  const arr = Array.isArray(lines) ? lines : String(lines || '').split(/\r?\n/)
  const cleanLines = arr.map(l => String(l || '').trim())
  const fullText = cleanLines.join('\n')
  const parseErrors = []

  const period = parseFnbStatementPeriod(fullText)
  if (!period) parseErrors.push('Could not parse Statement Period')

  const accountNumber = parseFnbAccountNumber(fullText)
  if (!accountNumber) parseErrors.push('Could not parse Account Number')

  const accountName = parseFnbAccountName(cleanLines)
  const balances = parseFnbBalances(cleanLines)
  const charges = parseFnbBankCharges(cleanLines)

  const debitTransactions = []
  const creditTransactions = []
  const accruedOnTransactions = []

  // Default categories used by suggestCategory if none passed in
  const cats = Array.isArray(categories) && categories.length > 0
    ? categories
    : DEFAULT_EXPENSE_CATEGORIES

  const year = period?.year
  for (const line of cleanLines) {
    const tx = parseFnbTransactionLine(line, year)
    if (!tx) continue

    // Auto-categorise — bank-side debits are best-effort, credits get
    // a sensible default of "Sales" (income) and don't need a real
    // expense category at all.
    const category = tx.type === 'debit'
      ? suggestCategory(tx.description, cats)
      : 'Sales'

    const record = {
      date: tx.date,
      description: tx.description,
      amount: tx.amount,
      category,
      // Credits are reconciled (we trust the bank), debits need user
      // confirmation before counting as an expense.
      confirmed: tx.type === 'credit',
    }

    if (tx.type === 'debit') debitTransactions.push(record)
    else creditTransactions.push(record)

    if (tx.accruedCharge != null && tx.accruedCharge > 0) {
      accruedOnTransactions.push({
        date: tx.date,
        description: tx.description,
        amount: tx.accruedCharge,
      })
    }
  }

  if (debitTransactions.length === 0 && creditTransactions.length === 0) {
    parseErrors.push('No transactions found in statement')
  }

  const totalDebits = debitTransactions.reduce((s, t) => s + t.amount, 0)
  const totalCredits = creditTransactions.reduce((s, t) => s + t.amount, 0)

  return {
    bankName: 'FNB',
    accountNumber: accountNumber || '',
    accountName: accountName || '',
    statementPeriod: period
      ? { from: period.from, to: period.to }
      : { from: '', to: '' },
    debitTransactions,
    creditTransactions,
    bankCharges: {
      ...charges,
      accruedOnTransactions,
    },
    openingBalance: balances.opening,
    closingBalance: balances.closing,
    totalDebits,
    totalCredits,
    parseErrors,
  }
}

// ─── Pluggable router ─────────────────────────────────────────────

/** Route a raw PDF text dump to the right bank parser. Currently only
 *  FNB is implemented. Other banks can be added by detecting their
 *  header signature and dispatching to a sibling parser. */
export function parseBankStatement(pdfText, bankName) {
  const text = String(pdfText || '')
  const explicit = String(bankName || '').toLowerCase()

  // Auto-detect FNB if not explicitly provided
  const looksLikeFnb = /first\s+national\s+bank|fnb|gold\s+business\s+account/i.test(text)

  if (explicit === 'fnb' || (!explicit && looksLikeFnb)) {
    return parseFnbStatement(text.split(/\r?\n/))
  }

  // Unknown bank — fall back to FNB heuristics so we still try to
  // recover transactions, but flag the format as unknown.
  const result = parseFnbStatement(text.split(/\r?\n/))
  return {
    ...result,
    bankName: explicit || 'unknown',
    parseErrors: [
      ...(result.parseErrors || []),
      'Bank not recognised — used FNB parser as fallback',
    ],
  }
}

/** Convenience wrapper: takes a File, runs PDF text extraction, then
 *  parses. Used by the UI in Phase 3. */
export async function parseBankStatementFile(file, { bankName, onProgress } = {}) {
  const text = await extractPdfText(file, onProgress)
  return parseBankStatement(text, bankName)
}
